import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { Inline } from '@atlaskit/primitives/compiled';
import { Tab, TabList } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';

import type { NLPSearchResultSource } from '../../../ai-answer-dialog/types';
import { SourcesStack } from '../../../common/ui/sources-stack/sources-stack';

import { messages } from './messages';

const styles = cssMap({
	fullHeight: {
		height: '100%',
	},
	tabListAndButtonInline: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingTop: token('space.075'),

		// this is a valid CSS selector needed to remove the border from the tab list
		// @ts-ignore -error - TS2353 - Object literal may only specify known properties
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& [role="tablist"]::before': {
			backgroundColor: 'transparent',
		},
	},
});

type AITabsHeaderProps =
	| {
			collapsed: boolean;
			onExpandCollapseButtonClick: () => void;
			showSourcesTab: boolean;
	  }
	| {
			collapsed?: undefined;
			onExpandCollapseButtonClick?: undefined;
			showSourcesTab: boolean;
	  };

export const AITabsHeader = ({
	collapsed,
	onExpandCollapseButtonClick,
	showSourcesTab,
}: AITabsHeaderProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Inline
			alignBlock="center"
			spread="space-between"
			testId="ai-tabs-header"
			xcss={styles.tabListAndButtonInline}
		>
			<TabList>
				<Tab>
					<FormattedMessage {...messages.ai_answer_card_tab_ai_answer} />
				</Tab>
				{showSourcesTab ? (
					<Tab>
						<FormattedMessage {...messages.ai_answer_card_tab_sources} />
					</Tab>
				) : null}
			</TabList>
			{typeof collapsed === 'boolean' ? (
				<IconButton
					appearance="subtle"
					icon={collapsed ? ChevronDownIcon : ChevronUpIcon}
					label={formatMessage(
						collapsed
							? messages.ai_answer_card_expand_button
							: messages.ai_answer_card_collapse_button,
					)}
					spacing="compact"
					onClick={onExpandCollapseButtonClick}
				/>
			) : null}
		</Inline>
	);
};

type AITabsHeaderWithSourcesStackProps =
	| {
			collapsed: boolean;
			onExpandCollapseButtonClick: () => void;
			sources?: NLPSearchResultSource[];
	  }
	| {
			collapsed?: undefined;
			onExpandCollapseButtonClick?: undefined;
			sources?: NLPSearchResultSource[];
	  };

export const AITabsHeaderWithSourcesStack = ({
	collapsed,
	onExpandCollapseButtonClick,
	sources,
}: AITabsHeaderWithSourcesStackProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Inline
			alignBlock="center"
			spread="space-between"
			testId="ai-tabs-header-with-sources-stack"
			xcss={styles.tabListAndButtonInline}
		>
			<TabList>
				<Tab>
					<FormattedMessage {...messages.ai_answer_card_tab_ai_answer} />
				</Tab>
				{sources && sources.length > 0 ? (
					<Tab>
						<Inline space="space.050" alignBlock="center">
							<>
								<SourcesStack sources={sources} />
								<FormattedMessage
									{...messages.ai_answer_card_tab_sources_with_pluralization}
									values={{
										sourcesCount: sources.length,
									}}
								/>
							</>
						</Inline>
					</Tab>
				) : null}
			</TabList>
			{typeof collapsed === 'boolean' ? (
				<IconButton
					appearance="subtle"
					icon={collapsed ? ChevronDownIcon : ChevronUpIcon}
					label={formatMessage(
						collapsed
							? messages.ai_answer_card_expand_button
							: messages.ai_answer_card_collapse_button,
					)}
					spacing="compact"
					onClick={onExpandCollapseButtonClick}
				/>
			) : null}
		</Inline>
	);
};
